import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * A plain, unbranded "Buy Now" button. Adds the selected variant to the cart
 * and redirects straight to standard Shopify checkout — unlike Shopify's
 * dynamic checkout buttons (Shop Pay / PayPal / Apple Pay), which are always
 * branded to whichever express payment method is enabled in Settings > Payments.
 *
 * @typedef {Object} Refs
 * @property {HTMLButtonElement} buyNowButton
 * @property {HTMLElement} buttonText
 * @property {HTMLElement} liveRegion
 * @property {HTMLElement} errorText
 *
 * @extends {Component<Refs>}
 */
class BuyNowButton extends Component {
  requiredRefs = ['buyNowButton', 'buttonText'];

  connectedCallback() {
    super.connectedCallback();
    this.variantId = this.dataset.variantId;
    this.defaultText = this.refs.buttonText.textContent;
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Keeps the button pointed at whichever pack is currently selected.
   * @param {CustomEvent} event
   */
  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        if (!detail) return;
        const resourceId = detail.resource?.id;

        if (resourceId) {
          this.variantId = resourceId;
          this.refs.buyNowButton.disabled = false;
          this.refs.buttonText.textContent = this.defaultText;
        } else if (detail.resource === null) {
          // Unavailable variant combination
          this.refs.buyNowButton.disabled = true;
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[buy-now-button] Event promise rejected:', error);
      });
  };

  /** @param {Event} event */
  handleClick(event) {
    event.preventDefault();
    if (!this.variantId || this.refs.buyNowButton.disabled) return;

    const { buyNowButton, buttonText, errorText } = this.refs;
    buyNowButton.disabled = true;
    buttonText.textContent = Theme.translations.added || 'Adding…';
    errorText?.classList.add('hidden');

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: this.variantId, quantity: 1 }] }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status) {
          throw new Error(data.message || 'Could not add item to cart');
        }
        window.location.href = '/checkout';
      })
      .catch((error) => {
        console.error('[buy-now-button]', error);
        buyNowButton.disabled = false;
        buttonText.textContent = this.defaultText;

        const message = error?.message || 'Something went wrong. Please try again.';
        if (errorText) {
          errorText.textContent = message;
          errorText.classList.remove('hidden');
        }
        if (this.refs.liveRegion) this.refs.liveRegion.textContent = message;
      });
  }
}

if (!customElements.get('buy-now-button')) {
  customElements.define('buy-now-button', BuyNowButton);
}
